import {
  UseLazyQuery,
  UseQuery
} from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { QueryDefinition } from "@reduxjs/toolkit/query";
import { isEqual } from "lodash";

import { emptySplitApi } from "services/empty-split-api";
import { safeRefetch } from "utils/services.util";

const DEFAULT_LIMIT = 5;
type QDef<QueryArg, ResultType> = QueryDefinition<
  QueryArg & { offset?: number; limit?: number },
  any,
  string,
  ResultType,
  string
>;

type UsePaginationProps<QueryArg> = {
  endpointName: string;
  params: QueryArg;
  limit?: number;
  options?: any;
  withTransition?: boolean;
  showCurrentData?: boolean;
};

export default function usePaginationV2<QueryArg, ResultType>({
  endpointName,
  limit = DEFAULT_LIMIT,
  params,
  options,
  withTransition,
  showCurrentData
}: UsePaginationProps<QueryArg>) {
  const [newOffset, setNewOffset] = useState<number | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [currentParams, setCurrentParams] = useState<QueryArg>(params);
  const [currentOptions, setCurrentOptions] = useState<any>(options);
  const [isDataPending, setIsDataPending] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { useQuery, useLazyQuery } = emptySplitApi.endpoints[
    endpointName as keyof typeof emptySplitApi.endpoints
  ] as {
    useQuery: UseQuery<
      QDef<
        QueryArg & { offset?: number; limit?: number },
        { [key: number]: ResultType[]; lastFetchedPage: number }
      >
    >;
    useLazyQuery: UseLazyQuery<
      QDef<
        QueryArg & { offset?: number; limit?: number },
        { [key: number]: ResultType[]; lastFetchedPage: number }
      >
    >;
  };

  const {
    data: queryData,
    refetch,
    isFetching,
    isSuccess,
    isUninitialized,
    isLoading: isInitialLoading,
    currentData
  } = useQuery(
    {
      ...currentParams,
      offset,
      limit
    },
    {
      ...currentOptions,
      // Always use the fresh skip value — currentOptions lags by 1 render
      // due to the useEffect sync, which can cause unwanted fetches.
      ...(options?.skip !== undefined && { skip: options.skip }),
      signal: abortControllerRef.current?.signal
    }
  );
  const pages = showCurrentData ? currentData : queryData;

  const [pageDataFetch] = useLazyQuery();

  const abortRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  }, []);

  useEffect(() => {
    if (options?.skip) return;
    abortRequests();
  }, [params, options, abortRequests]);

  const { data, pageMap } = useMemo(() => {
    const data = [];
    const pageMap = {} as { [dataId: number]: number };
    for (let i = 0; i < (pages?.lastFetchedPage || 0) + 1; i++) {
      const currentData = pages?.[i] || [];
      currentData.reduce((acc: { [dataId: number]: number }, item) => {
        acc[(item as ResultType & { id: number }).id] = i;
        return acc;
      }, pageMap);
      data.push(...currentData);
    }
    return { data, pageMap };
  }, [pages]);

  const isLoading = useMemo(
    () =>
      Boolean(
        !options?.skip && (isFetching || isUninitialized || isDataPending)
      ),
    [isDataPending, isFetching, isUninitialized, options?.skip]
  );
  const hasBeenInitiallyLoaded = useMemo(
    () => Boolean(!isUninitialized && !isInitialLoading),
    [isInitialLoading, isUninitialized]
  );
  const clearData = useCallback(() => {
    setOffset(0);
    setHasMoreData(true);
    setNewOffset(undefined);
    setIsDataPending(true);
  }, []);

  const fetchMore = useCallback(() => {
    if (hasMoreData && !isLoading && newOffset === undefined) {
      setNewOffset(offset + limit);
    }
  }, [isLoading, offset, limit, newOffset, hasMoreData]);

  const updatePaginatedData = useCallback(
    (dataId: number) => {
      const page = pageMap[dataId] ?? 1;
      const pageOffset = page * limit;
      pageDataFetch({ ...currentParams, offset: pageOffset, limit });
    },
    [pageMap, currentParams, pageDataFetch, limit]
  );

  useEffect(() => {
    if (newOffset !== undefined && newOffset !== offset) {
      const updateFn = () => {
        if (newOffset === 0) {
          clearData();
        } else {
          setOffset(newOffset);
        }
      };
      if (withTransition) {
        requestAnimationFrame(() => {
          updateFn();
        });
      } else {
        updateFn();
      }
    }
  }, [offset, newOffset, clearData, withTransition]);

  useEffect(() => {
    if (!isEqual(params, currentParams) || !isEqual(options, currentOptions)) {
      // When skip is active, freeze internal params/options so useQuery never
      // sees new query args while it should be dormant.
      if (options?.skip) return;
      clearData();
      setCurrentParams(params);
      setCurrentOptions(options);
    }
  }, [params, currentParams, clearData, options, currentOptions]);

  useEffect(() => {
    if (
      !hasMoreData ||
      isLoading ||
      !pages ||
      !pages[pages?.lastFetchedPage || 0]
    )
      return;

    if (pages[pages?.lastFetchedPage || 0].length < limit) {
      setHasMoreData(false);
    } else {
      setHasMoreData(true);
    }
  }, [pages, limit, isLoading, hasMoreData, offset, clearData]);

  useEffect(() => {
    if (
      newOffset === offset &&
      isSuccess &&
      pages?.lastFetchedPage === Math.abs(offset / limit)
    ) {
      setNewOffset(undefined);
    }
  }, [
    setNewOffset,
    newOffset,
    isSuccess,
    offset,
    limit,
    pages?.lastFetchedPage
  ]);

  useEffect(() => {
    setIsDataPending(false);
  }, [data]);

  const refetchData = useCallback(() => {
    clearData();
    safeRefetch(refetch, isUninitialized);
  }, [refetch, clearData, isUninitialized]);

  return {
    refetch: refetchData,
    isFetching,
    data,
    isLoading,
    isSuccess,
    isUninitialized,
    hasBeenInitiallyLoaded,
    hasMoreData,
    fetchMore,
    clearData,
    offset,
    isDataPending,
    updatePaginatedData,
    abortController: abortControllerRef.current,
    abortRequests
  };
}
